import { ComponentRef } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AngularUtilService,
  Column,
  ColumnEditor,
  Editor,
  EditorValidator,
  EditorValidatorOutput,
  GridOption
} from 'angular-slickgrid';
import * as moment from 'moment';

/*
 * An example of a 'detached' editor.
 * KeyDown events are also handled to provide handling for Tab, Shift-Tab, Esc and Ctrl-Enter.
 */
export class CustomAngularComponentEditor implements Editor {
  changeSubscriber: Subscription;
  changedValueSubscriber: Subscription;

  /** Angular Component Reference */
  componentRef: ComponentRef<any>;

  /** default item Id */
  defaultId: string;

  /** default item object */
  defaultItem: any;

  /** SlickGrid grid object */
  grid: any;

  selectedIem: any;
  controlType: any;

  isSave = false;

  constructor(private args: any) {
    this.grid = args && args.grid;
    this.init();
  }

  /** Angular Util Service (could be inside the Grid Options Params or the Editor Params ) */
  get angularUtilService(): AngularUtilService {
    let angularUtilService = this.gridOptions && this.gridOptions.params && this.gridOptions.params.angularUtilService;
    if (!angularUtilService || !(angularUtilService instanceof AngularUtilService)) {
      angularUtilService = this.columnEditor && this.columnEditor.params && this.columnEditor.params.angularUtilService;
    }
    return angularUtilService;
  }

  /** Get the Collection */
  get collection(): any[] {
    return this.columnDef && this.columnDef.internalColumnEditor.collection || [];
  }

  /** Get Column Definition object */
  get columnDef(): Column {
    return this.args && this.args.column || {};
  }

  /** Get Column Editor object */
  get columnEditor(): ColumnEditor {
    return this.columnDef && this.columnDef.internalColumnEditor || {};
  }

  /** Getter for the Grid Options pulled through the Grid Object */
  get gridOptions(): GridOption {
    return (this.grid && this.grid.getOptions) ? this.grid.getOptions() : {};
  }

  get hasAutoCommitEdit() {
    return this.gridOptions.autoCommitEdit;
  }

  /** Get the Validator function, can be passed in Editor property or Column Definition */
  get validator(): EditorValidator {
    return this.columnEditor.validator || this.columnDef.validator;
  }

  init() {
    if (!this.columnEditor || !this.columnEditor.params.component ||
      !(this.angularUtilService instanceof AngularUtilService)) {
      throw new Error(`[Angular-Slickgrid] For Editor with Angular Component to work properly,
       you need to provide your component to the "component" property and make sure to add it to your "entryComponents" array.
      You also need to provide the "AngularUtilService" via the Editor Params OR the Grid Options Params
      Example: this.columnDefs = [{ id: 'title', field: 'title',
                                    editor: { model: CustomAngularComponentEditor, collection: [...],
                                    params: { component: MyComponent, angularUtilService: this.angularUtilService }}];
      OR this.columnDefs = [{ id: 'title', field: 'title',
                            editor: { model: CustomAngularComponentEditor, collection: [...] }];
      this.gridOptions = { params: { angularUtilService: this.angularUtilService }}`);
    }
    if (this.columnEditor && this.columnEditor.params.component) {
      const componentOutput = this.angularUtilService.createAngularComponentAppendToDom(this.columnEditor.params.component, this.args.container);
      this.componentRef = componentOutput && componentOutput.componentRef;
      // const configs = this.columnEditor.params.configs;
      // configs['updateSource'] = this.updateDatasource; 
      // Object.assign(this.columnEditor.params.configs, configs);
      // this.columnEditor.params.configs['updateCombo'] = { 
      //   setSource: this.updateDatasource
      // };

      // here we override the collection object of the Angular Component
      // but technically you can pass any values you wish to your Component
      if (this.collection && this.columnDef.internalColumnEditor.params.configs.type !== 'datePicker') {
        this.controlType = 'autoComplete';
        if (this.columnDef.internalColumnEditor.params.configs['isMultiselect']) {
          Object.assign(this.componentRef.instance, {
            list: this.collection,
            configs: this.columnDef.internalColumnEditor.params.configs
          });
        } else {
          Object.assign(this.componentRef.instance, {
            datasource: this.collection,
            configs: this.columnDef.internalColumnEditor.params.configs
          });
        }
      }

      if (this.columnDef.internalColumnEditor.params.configs.type === 'datePicker') {
        this.controlType = 'datePicker';
        Object.assign(this.componentRef.instance, { configs: this.columnDef.internalColumnEditor.params.configs });
      }

      // when our model (item object) changes, we'll call a save of the slickgrid editor
      this.changeSubscriber = this.componentRef.instance.onItemChanged.subscribe((item: any) => {
        this.setValue(item);
        this.save();
      });

      this.changedValueSubscriber = this.componentRef.instance.onValueChanged.subscribe((item: any) => {
        if (!item) { return; }
        this.selectedIem = item;
        this.args.item[this.args.column.field] = item;
        this.setValue(item);
      });
    }
  }

  setValue(item: any) {
    if (this.controlType === 'autoComplete') {
      this.componentRef.instance.selectedId = item.id;
      this.componentRef.instance.selectedItem = item;
      this.selectedIem = item;
      // this.args.item[this.args.column.field] = item;
    } else if (this.controlType === 'datePicker') {
      this.componentRef.instance.selectedItem = item;
    }
  }

  save() {
    const validation = this.validate();
    if (validation && validation.valid) {
      if (this.hasAutoCommitEdit) {
        this.args.grid.getEditorLock().commitCurrentEdit();
      } else {
        this.args.commitChanges();
      }
      this.isSave = true;
    }
  }

  cancel() {
    this.componentRef.instance.selectedId = this.defaultId;
    this.componentRef.instance.selectedItem = this.defaultItem;
    if (this.args && this.args.cancelChanges) {
      this.args.cancelChanges();
    }
  }

  /** optional, implement a hide method on your Angular Component */
  hide() {
    if (this.componentRef && this.componentRef.instance && typeof this.componentRef.instance.hide === 'function') {
      this.componentRef.instance.hide();
    }
  }

  /** optional, implement a show method on your Angular Component */
  show() {
    if (this.componentRef && this.componentRef.instance && typeof this.componentRef.instance.show === 'function') {
      this.componentRef.instance.show();
    }
  }

  /** destroy the Angular Component & Subscription */
  destroy() {
    if (this.componentRef && this.componentRef.destroy && this.isSave) {
      this.componentRef.destroy();
      this.changeSubscriber.unsubscribe();
    }
  }

  /** optional, implement a focus method on your Angular Component */
  focus() {
    if (this.componentRef && this.componentRef.instance && typeof this.componentRef.instance.focus === 'function') {
      this.componentRef.instance.focus();
    }
  }

  applyValue(item: any, state: any) {
    let dateParts;
    if (this.controlType === 'datePicker') {
      if (state instanceof Date) {
        const date = moment(state).format(this.columnDef['dateFormat']);
        if (this.columnDef.internalColumnEditor.params.configs.pickerType === 'both') {
          dateParts = date.split(' ');
          item[this.columnDef.field] = dateParts[0];
          if (this.columnDef['timeField']) {
            item[this.columnDef['timeField']] = dateParts[1] ? dateParts[1] : '';
          }
        } else {
          item[this.columnDef.field] = date;
        }
      } else if (state) {
        dateParts = state.split(' ');
        item[this.columnDef.field] = dateParts[0];
        if (this.columnDef['timeField']) {
          item[this.columnDef['timeField']] = dateParts[1] ? dateParts[1] : '';
        }
      } else if (!state) {
        item[this.columnDef.field] = '';
        if (this.columnDef['timeField']) {
          item[this.columnDef['timeField']] = '';
        }
      }

    } else {
      item[this.columnDef.field] = this.selectedIem ? this.selectedIem : state;
    }
  }

  getValue() {
    return this.componentRef.instance.selectedItem;
  }

  loadValue(item: any) {
    const itemObject = item && item[this.columnDef.field];
    if (this.controlType === 'autoComplete') {
      this.componentRef.instance.selectedId = itemObject && itemObject.id || '';
      this.componentRef.instance.selectedItem = itemObject && itemObject;
      this.columnEditor.params.configs.model = itemObject && itemObject;
    } else if (this.controlType === 'datePicker') {
      let date;
      if (this.columnDef['timeField'] && item[this.columnDef['timeField']]) {
        date = item[this.columnDef.field] + ' ' + item[this.columnDef['timeField']]
      } else if (item[this.columnDef.field]) {
        const itemString = item[this.columnDef.field];
        let str = itemString.split(' ').splice(-1);
        const splittedTimeVal = str[0].split(':');
        date = moment().set({ 'hour': splittedTimeVal[0], 'minute': splittedTimeVal[1] });
        date = date ? date._d : null;
      }
      const selValue = (date) ? moment(date, this.columnDef['dateFormat'])['_d'] : '';
      this.componentRef.instance.selectedItem = selValue;
      this.columnDef['model'] = selValue;
      this.args.column.model = selValue;
      this.columnEditor.params.configs.model = selValue;
    }
  }

  serializeValue(): any {
    return this.componentRef.instance.selectedItem;
  }

  isValueChanged() {
    if (this.controlType === 'autoComplete') {
      return (!(this.componentRef.instance.selectedItem && this.componentRef.instance.selectedItem.id === '' && this.defaultId == null)) &&
        (this.componentRef.instance.selectedItem && this.componentRef.instance.selectedItem.id !== this.defaultId);
    } else if (this.controlType === 'datePicker') {
      return (!(this.componentRef.instance.selectedItem === '' && this.defaultId == null)) &&
        (this.componentRef.instance.selectedItem !== this.defaultId);
    }
  }

  validate(): EditorValidatorOutput {
    if (this.controlType === 'autoComplete') {
      if (this.validator) {
        const value = this.componentRef.instance.selectedId;
        return this.validator(value, this.args);
      }
    } else if (this.controlType === 'datePicker') {
      if (this.validator) {
        const value = this.componentRef.instance.selectedItem;
        return this.validator(value, this.args);
      }
    }

    // by default the editor is always valid
    // if user want it to be required, he would have to provide his own validator
    return {
      valid: true,
      msg: null
    };
  }

  updateDatasource() {
    if (this.collection && this.collection.length) {
      this.controlType = 'autoComplete';
      Object.assign(this.componentRef.instance, {
        datasource: this.collection,
        configs: this.columnDef.internalColumnEditor.params.configs
      });
    }
  }

}
